import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faAnglesLeft, faAnglesRight } from '@fortawesome/free-solid-svg-icons';
import HOTDataGrid from './HOTDataGrid';
import { getHotLicenseKey } from '../../../services/configService';
import type { BaseEntity, BaseExplorerConfig, ColumnConfig } from '../../../types/baseExplorerConfig';
import type { SortConfig } from './types';

interface ResultsSectionProps<T extends BaseEntity> {
  entities: T[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  sortConfig: SortConfig | null;
  columns: ColumnConfig[];
  features: any;
  config: BaseExplorerConfig<T>;
  onEntitySelect?: (entity: T) => void;
  onEntityView?: (entity: T) => void;
  onEntityEdit?: (entity: T) => void;
  onEntityDelete?: (entity: T) => void;
  onSort: (field: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  transformFieldValue: (entity: T, field: keyof T) => string;
  onRetry: () => void;
  isWindowed: boolean;
  onFilterClick?: (field: string) => void;
  activeFilters?: Record<string, any>;
  /** Per-column widths previously saved by this user. Applied on mount;
   *  changes via afterColumnResize bubble back through onColumnResize. */
  savedColumnWidths?: Record<string, number>;
  onColumnResize?: (field: string, width: number) => void;
}

function ResultsSectionInner<T extends BaseEntity>({
  entities,
  loading,
  error,
  totalCount,
  currentPage,
  pageSize,
  sortConfig,
  columns,
  features,
  config,
  onEntitySelect,
  onEntityView,
  onEntityEdit,
  onEntityDelete,
  onSort,
  onPageChange,
  onPageSizeChange,
  transformFieldValue,
  onRetry,
  isWindowed,
  onFilterClick,
  activeFilters,
  savedColumnWidths,
  onColumnResize,
}: ResultsSectionProps<T>) {
  const totalPages = Math.ceil(totalCount / pageSize);
  const pageSizeOptions: number[] = features.pagination?.pageSizeOptions ?? [];

  // Editable page-number input. Local string state lets the user type freely;
  // commit (parse + clamp + onPageChange) happens on blur or Enter. Sync from
  // the prop so external page changes (Next/Prev buttons) update the display.
  const [pageInput, setPageInput] = useState(String(currentPage));
  useEffect(() => { setPageInput(String(currentPage)); }, [currentPage]);
  const commitPageInput = () => {
    const n = parseInt(pageInput, 10);
    if (!Number.isFinite(n) || totalPages < 1) {
      setPageInput(String(currentPage));
      return;
    }
    const clamped = Math.min(Math.max(1, n), totalPages);
    if (clamped !== currentPage) onPageChange(clamped);
    setPageInput(String(clamped));
  };

  return (
    <div
      className="explorer-content"
      style={{
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {error && (
        <div className="error-state">
          <p className="error-message">{error}</p>
          <button onClick={onRetry} className="btn btn-secondary">Retry</button>
        </div>
      )}

      {!error && (
        <>
          <div className="data-table-container" style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden', position: 'relative' }}>
            {loading && (
              <div
                className="loading-state"
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.6)',
                  pointerEvents: 'none',
                }}
              >
                <div className="loading-spinner" />
                <p>Loading {config.entityName.toLowerCase()} data...</p>
              </div>
            )}
            <HOTDataGrid<T>
              data={entities}
              columns={columns}
              sortConfig={sortConfig}
              onSort={onSort}
              transformFieldValue={transformFieldValue}
              features={features}
              config={config}
              onEntitySelect={onEntitySelect}
              onEntityView={onEntityView}
              onEntityEdit={onEntityEdit}
              onEntityDelete={onEntityDelete}
              licenseKey={getHotLicenseKey()}
              height="100%"
              onFilterClick={onFilterClick}
              activeFilters={activeFilters}
              savedColumnWidths={savedColumnWidths}
              onColumnResize={onColumnResize}
            />
          </div>

          {features.pagination?.enabled && (
            <div
              className="pagination-section"
              style={{
                flexShrink: 0,
                backgroundColor: 'var(--em3-navigation-bg)',
                color: 'var(--em3-navigation-text)',
                borderTop: '1px solid var(--panel-border, #404040)',
                padding: '8px 16px',
              }}
            >
              <div className="pagination-info">
                <span className="pagination-info__verbose">
                  Showing {((currentPage - 1) * pageSize) + 1} to{' '}
                  {Math.min(currentPage * pageSize, totalCount)} of{' '}
                  {totalCount} {config.entityName.toLowerCase()}s
                </span>
                <span className="pagination-info__compact">
                  {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, totalCount)} of {totalCount}
                </span>
                {pageSizeOptions.length > 1 && (
                  <label
                    className="pagination-page-size"
                    style={{ marginLeft: '16px' }}
                  >
                    <span className="pagination-info__verbose">Rows per page:&nbsp;</span>
                    <select
                      value={pageSize}
                      onChange={(e) => onPageSizeChange(Number(e.target.value))}
                      aria-label="Rows per page"
                    >
                      {pageSizeOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              {totalCount > pageSize && (
                <div className="pagination-controls">
                  <button
                    onClick={() => onPageChange(1)}
                    disabled={currentPage === 1}
                    className="btn-neutral pagination-nav"
                    aria-label="First page"
                    title="First page"
                  >
                    <FontAwesomeIcon icon={faAnglesLeft} />
                  </button>
                  <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="btn-neutral pagination-nav"
                    aria-label="Previous page"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                    <span className="btn-label">Previous</span>
                  </button>
                  <span className="page-number">
                    <span className="pagination-info__verbose">Page </span>
                    <input
                      type="number"
                      min={1}
                      max={totalPages}
                      value={pageInput}
                      onChange={(e) => setPageInput(e.target.value)}
                      onBlur={commitPageInput}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
                      }}
                      onFocus={(e) => e.currentTarget.select()}
                      aria-label="Go to page"
                      className="page-input"
                      style={{
                        width: `${Math.max(2, String(totalPages).length) + 2}ch`,
                        textAlign: 'center',
                        padding: '2px 4px',
                        backgroundColor: 'var(--em3-input-bg, #2a2a2a)',
                        color: 'inherit',
                        border: '1px solid var(--panel-border, #404040)',
                        borderRadius: '3px',
                        margin: '0 4px',
                      }}
                    />
                    <span className="pagination-info__compact">/</span><span className="pagination-info__verbose"> of </span>{totalPages}
                  </span>
                  <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="btn-neutral pagination-nav"
                    aria-label="Next page"
                  >
                    <span className="btn-label">Next</span>
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                  <button
                    onClick={() => onPageChange(totalPages)}
                    disabled={currentPage >= totalPages}
                    className="btn-neutral pagination-nav"
                    aria-label="Last page"
                    title="Last page"
                  >
                    <FontAwesomeIcon icon={faAnglesRight} />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const ResultsSection = React.memo(ResultsSectionInner) as <T extends BaseEntity>(
  props: ResultsSectionProps<T>,
) => React.ReactElement;

export default ResultsSection;
